import Vue from 'vue'

// Reactive state for Vue 2
const state = Vue.observable({
  courses: [],
  users: [],
  progress: [],
  enrollments: []
})

// Categories and difficulties constants
const CATEGORIES = ['Compliance', 'Product Training', 'Soft Skills', 'Technical']
const DIFFICULTIES = ['Beginner', 'Intermediate', 'Advanced']

// ---------- Mapper Functions ----------

function mapCourseFromBackend(c) {
  const instructorName = c.createdBy?.systemUserId || 'Instructor'
  
  let diff = 'Beginner'
  if (c.difficultyLevel === 'INTERMEDIATE') diff = 'Intermediate'
  else if (c.difficultyLevel === 'ADVANCED') diff = 'Advanced'

  const lessons = []
  ;(c.modules || []).forEach(m => {
    (m.lessons || []).forEach(l => {
      // Pick first VIDEO content
      const videoContent = (l.contents || []).find(ct => ct.contentType === 'VIDEO')
      const hlsStatus = videoContent?.videoDetails?.hlsStatus || null
      const videoUrl = 'https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/BigBuckBunny.mp4'

      lessons.push({
        _id: l._id || l.lessonId,
        moduleId: m._id || m.moduleId,
        title: l.lessonTitle,
        description: l.lessonDescription || '',
        duration: l.estimatedDurationMinutes ? `${l.estimatedDurationMinutes}:00` : '10:00',
        videoUrl,
        hlsStatus,
        contentId: videoContent?._id || videoContent?.contentId || null,
        order: l.lessonOrder || 1
      })
    })
  })

  return {
    _id: c._id,
    title: c.courseName,
    description: c.courseDescription || '',
    category: c.category || 'Product Training',
    difficulty: diff,
    instructor: instructorName,
    thumbnail: c.thumbnail || '',
    createdAt: c.createdAt,
    enrolledUsers: [], // Will be populated from enrollments
    lessons: lessons,
    rawCourse: c
  }
}

// ---------- API / Helper Methods ----------

async function initLearningData(axios) {
  try {
    // 1. Fetch all courses
    const coursesRes = await axios.get('/learning_center/courses?limit=100&isPublished=false')
    const rawCourses = coursesRes.data.courses || []
    
    // 2. Fetch all user enrollments
    const enrollmentsRes = await axios.get('/learning_center/enrollments?limit=100')
    const enrollments = enrollmentsRes.data.enrollments || []
    state.enrollments = enrollments

    // 3. Map courses and associate enrolled users
    const mappedCourses = rawCourses.map(c => {
      const course = mapCourseFromBackend(c)
      // Filter enrollments for this course
      const courseEnrollments = enrollments.filter(e => 
        (e.course?._id || e.course)?.toString() === course._id.toString()
      )
      course.enrolledUsers = courseEnrollments.map(e => e.enrolledUser?._id || e.enrolledUser)
      return course
    })

    // Populate active users/employees from the organization
    const usersRes = await axios.get('/v3/users?limit=*&sortBy=firstName&sortOrder=asc')
    const rawUsers = usersRes.data.users || []
    const mappedUsers = rawUsers.map(u => {
      const name = `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.systemUserId?.firstName || 'User'
      const email = u.email || u.systemUserId?.email || 'N/A'
      const department = u.department?.name || 'Collections'
      return {
        _id: u._id,
        name,
        avatar: '',
        email,
        department
      }
    })

    // 4. Populate progressEntries from module progress
    const progressList = []
    enrollments.forEach(e => {
      const userId = e.enrolledUser?._id || e.enrolledUser
      const courseId = e.course?._id || e.course
      ;(e.moduleProgress || []).forEach(mp => {
        (mp.lessonProgress || []).forEach(lp => {
          if (lp.isLessonCompleted) {
            progressList.push({
              lessonId: lp.lessonId,
              courseId: courseId,
              userId: userId,
              completedAt: lp.lessonCompletedDate || new Date().toISOString()
            })
          }
        })
      })
    })

    state.courses = mappedCourses
    state.users = mappedUsers
    state.progress = progressList
  } catch (err) {
    console.error('Error loading learning data from API:', err)
    throw err
  }
}

function getCourseById(courseId) {
  return state.courses.find(c => c._id === courseId) || null
}

function getUserById(userId) {
  return state.users.find(u => u._id === userId) || null
}

// Access state.progress length inside helper to establish reactive track dependencies
function isLessonCompleted(courseId, lessonId, userId) {
  void state.progress.length
  return state.progress.some(
    p => p.courseId === courseId && p.lessonId === lessonId && p.userId === userId
  )
}

function getCourseProgress(courseId, userId) {
  const course = getCourseById(courseId)
  if (!course) return 0
  const totalLessons = course.lessons.length
  if (totalLessons === 0) return 0
  const completedLessons = course.lessons.filter(l =>
    isLessonCompleted(courseId, l._id, userId)
  ).length
  return Math.round((completedLessons / totalLessons) * 100)
}

function getCompletedLessonsCount(courseId, userId) {
  const course = getCourseById(courseId)
  if (!course) return 0
  return course.lessons.filter(l =>
    isLessonCompleted(courseId, l._id, userId)
  ).length
}

async function markLessonComplete(axios, courseId, lessonId, userId) {
  try {
    const enrollment = state.enrollments.find(e => 
      (e.course?._id || e.course)?.toString() === courseId.toString() &&
      (e.enrolledUser?._id || e.enrolledUser)?.toString() === userId.toString()
    )
    if (!enrollment) return

    await axios.put(`/learning_center/progress/enrollment/${enrollment._id}/lesson/${lessonId}/complete`)
    
    if (!isLessonCompleted(courseId, lessonId, userId)) {
      state.progress.push({
        lessonId,
        userId,
        courseId,
        completedAt: new Date().toISOString()
      })
    }
  } catch (err) {
    console.error('Error marking lesson complete:', err)
  }
}

async function markLessonIncomplete(axios, courseId, lessonId, userId) {
  // Frontend fallback only
  const idx = state.progress.findIndex(
    p => p.courseId === courseId && p.lessonId === lessonId && p.userId === userId
  )
  if (idx > -1) {
    state.progress.splice(idx, 1)
  }
}

async function addCourse(axios, courseData) {
  try {
    const payload = {
      courseName: courseData.title,
      courseDescription: courseData.description,
      category: courseData.category,
      difficultyLevel: courseData.difficulty.toUpperCase(),
      enrollmentType: 'SELF_ENROLL'
    }

    const res = await axios.post('/learning_center/courses', payload)
    const newCourseRaw = res.data.course

    // Auto-create a default general module for lessons
    const moduleRes = await axios.post(`/learning_center/courses/${newCourseRaw._id}/modules`, {
      moduleName: 'General Content',
      moduleDescription: 'General lessons for this course'
    })

    const mapped = mapCourseFromBackend(moduleRes.data.course)
    state.courses.push(mapped)
    return mapped
  } catch (err) {
    console.error('Error creating course:', err)
    throw err
  }
}

async function updateCourse(axios, courseId, courseData) {
  try {
    const payload = {
      courseName: courseData.title,
      courseDescription: courseData.description,
      category: courseData.category,
      difficultyLevel: courseData.difficulty.toUpperCase()
    }

    const res = await axios.put(`/learning_center/courses/${courseId}`, payload)
    const mapped = mapCourseFromBackend(res.data.course)
    
    const idx = state.courses.findIndex(c => c._id === courseId)
    if (idx > -1) {
      state.courses.splice(idx, 1, mapped)
    }
    return mapped
  } catch (err) {
    console.error('Error updating course:', err)
    throw err
  }
}

async function deleteCourse(axios, courseId) {
  try {
    await axios.delete(`/learning_center/courses/${courseId}`)
    const idx = state.courses.findIndex(c => c._id === courseId)
    if (idx > -1) {
      state.courses.splice(idx, 1)
      state.progress = state.progress.filter(p => p.courseId !== courseId)
    }
  } catch (err) {
    console.error('Error deleting course:', err)
    throw err
  }
}

async function addLesson(axios, courseId, lessonData) {
  try {
    const course = state.courses.find(c => c._id === courseId)
    if (!course) return null

    // Get the first module ID (default is "General Content")
    const moduleId = course.rawCourse.modules?.[0]?._id

    if (!moduleId) {
      throw new Error('Course has no modules to attach lesson')
    }

    const payload = {
      lessonTitle: lessonData.title,
      lessonDescription: lessonData.description,
      estimatedDurationMinutes: parseInt(lessonData.duration) || 10
    }

    const res = await axios.post(`/learning_center/courses/${courseId}/modules/${moduleId}/lessons`, payload)
    const mapped = mapCourseFromBackend(res.data.course)

    const idx = state.courses.findIndex(c => c._id === courseId)
    if (idx > -1) {
      state.courses.splice(idx, 1, mapped)
    }
    return mapped.lessons[mapped.lessons.length - 1]
  } catch (err) {
    console.error('Error adding lesson:', err)
    throw err
  }
}

async function updateLesson(axios, courseId, lessonId, lessonData) {
  const course = getCourseById(courseId)
  if (!course) return null
  const lesson = course.lessons.find(l => l._id === lessonId)
  if (lesson) {
    Object.assign(lesson, {
      title: lessonData.title,
      description: lessonData.description,
      duration: lessonData.duration
    })
  }
  return lesson
}

async function deleteLesson(axios, courseId, lessonId) {
  const course = getCourseById(courseId)
  if (!course) return
  const idx = course.lessons.findIndex(l => l._id === lessonId)
  if (idx > -1) {
    course.lessons.splice(idx, 1)
  }
}

function getLastActivity(courseId, userId) {
  const entries = state.progress.filter(p => p.courseId === courseId && p.userId === userId)
  if (entries.length === 0) return null
  const sorted = entries.sort((a, b) => new Date(b.completedAt) - new Date(a.completedAt))
  return sorted[0].completedAt
}

function getOverallStats() {
  const totalCourses = state.courses.length
  const totalLessons = state.courses.reduce((sum, c) => sum + c.lessons.length, 0)
  const enrolledUserIds = new Set()
  state.courses.forEach(c => c.enrolledUsers.forEach(uid => enrolledUserIds.add(uid)))
  const totalEnrolled = enrolledUserIds.size

  let totalPercent = 0
  let pairCount = 0
  state.courses.forEach(c => {
    c.enrolledUsers.forEach(uid => {
      totalPercent += getCourseProgress(c._id, uid)
      pairCount++
    })
  })
  const avgCompletion = pairCount > 0 ? Math.round(totalPercent / pairCount) : 0

  return { totalCourses, totalLessons, totalEnrolled, avgCompletion }
}

function getUserProgressTable() {
  const rows = []
  state.users.forEach(user => {
    state.courses.forEach(course => {
      const isEnrolled = course.enrolledUsers.includes(user._id)
      const completed = isEnrolled ? getCompletedLessonsCount(course._id, user._id) : 0
      const total = course.lessons.length
      const percent = isEnrolled && total > 0 ? Math.round((completed / total) * 100) : 0
      const lastAct = isEnrolled ? getLastActivity(course._id, user._id) : null
      rows.push({
        userName: user.name,
        userEmail: user.email,
        department: user.department,
        userId: user._id,
        courseId: course._id,
        courseTitle: course.title,
        completedLessons: completed,
        totalLessons: total,
        progress: percent,
        lastActivity: lastAct,
        isEnrolled
      })
    })
  })
  return rows
}

export {
  state,
  CATEGORIES,
  DIFFICULTIES,
  initLearningData,
  getCourseById,
  getUserById,
  isLessonCompleted,
  getCourseProgress,
  getCompletedLessonsCount,
  markLessonComplete,
  markLessonIncomplete,
  addCourse,
  updateCourse,
  deleteCourse,
  addLesson,
  updateLesson,
  deleteLesson,
  getLastActivity,
  getOverallStats,
  getUserProgressTable
}
